import { z } from "zod";

import { InteractionTypeEnum } from "@examspark/types";

import {
  PromptSchema,
  ImageSchema,
  OptionSchema,
  TextSchema,
  SpotSchema,
  TokenSchema,
  GapSchema,
  PairSchema,
  ConfigSchema,
  ScoringSchema,
  FractionShapeEnum,
} from "./item-stem.zod";

export const InteractionTypeSchema = z.nativeEnum(InteractionTypeEnum);

// base stem schema
const StemBaseSchema = z.object({
  id: z.string().min(1),
  type: z.literal("stem"),
  content: z.object({
    prompt: PromptSchema,
  }),
  config: ConfigSchema.partial().optional(),
  scoring: ScoringSchema.partial().optional(),
  language: z.string().optional(),
});

// content base schemas
const StemContentBaseSchema = z.object({
  prompt: PromptSchema,
});

const StemContentBaseGraphicSchema = StemContentBaseSchema.extend({
  image: ImageSchema.optional(),
  spots: z.array(SpotSchema).optional(),
});

// specific stem schemas
export const StemAudioSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.Audio),
});

export const StemTextGapMatchSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.TextGapMatch),
  content: StemContentBaseSchema.extend({
    text: TextSchema,
    gaps: z.record(GapSchema),
    options: z.array(OptionSchema),
  }),
  scoring: z
    .object({
      pairs: z.array(PairSchema),
    })
    .optional(),
});

export const StemGraphicGapMatchSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.GraphicGapMatch),
  content: StemContentBaseGraphicSchema.extend({
    tokens: z.array(TokenSchema).optional(),
  }),
  scoring: z
    .object({
      pairs: z.array(PairSchema),
    })
    .optional(),
});

export const StemGraphicSelectPointSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.GraphicSelectPoint),
  content: StemContentBaseGraphicSchema,
  scoring: z
    .object({
      spots: z.record(
        z.object({
          id: z.string(),
          isKey: z.boolean(),
        }),
      ),
    })
    .optional(),
});

export const StemTextInlineSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.TextInline),
  content: StemContentBaseSchema.extend({
    text: TextSchema,
    gaps: z.record(GapSchema),
  }),
  scoring: z.record(z.any()).optional(),
});

export const StemGraphicOrderSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.GraphicOrder),
  content: StemContentBaseGraphicSchema,
  scoring: z
    .object({
      order: z.array(z.string()),
    })
    .optional(),
});

export const StemCartesianInequalitySchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.CartesianInequality),
  config: z
    .object({
      graph: z.object({
        width: z.number(),
        height: z.number(),
        x: z.object({
          min: z.number(),
          max: z.number(),
          step: z.number().optional(),
        }),
        y: z.object({
          min: z.number(),
          max: z.number(),
          step: z.number().optional(),
        }),
      }),
    })
    .optional(),
  scoring: z
    .object({
      inequality: z.any(),
    })
    .optional(),
});

export const StemChoiceSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.Choice),
  content: StemContentBaseSchema.extend({
    options: z.array(OptionSchema),
  }),
});

export const StemBarChartSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.BarChart),
  content: StemContentBaseSchema.extend({
    data: z.array(
      z.object({
        label: z.string(),
        value: z.number(),
      }),
    ),
  }),
  config: z
    .object({
      graph: z.any(),
    })
    .optional(),
  scoring: z
    .object({
      data: z.array(z.any()),
    })
    .optional(),
});

export const StemLineChartSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.LineChart),
  content: StemContentBaseSchema.extend({
    data: z.array(
      z.object({
        label: z.string(),
        value: z.number(),
      }),
    ),
  }),
  config: z
    .object({
      graph: z.any(),
    })
    .optional(),
  scoring: z
    .object({
      data: z.array(z.any()),
    })
    .optional(),
});

export const StemGraphicHotspotSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.GraphicHotspot),
  content: StemContentBaseGraphicSchema,
});

export const StemGraphicPositionObjectSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.GraphicPositionObject),
  content: StemContentBaseGraphicSchema.extend({
    tokens: z.array(TokenSchema).optional(),
  }),
});

export const StemGraphicAssociateSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.GraphicAssociate),
  content: StemContentBaseGraphicSchema,
  scoring: z
    .object({
      pairs: z.array(PairSchema),
    })
    .optional(),
});

export const StemTextSelectableSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.TextSelectable),
  content: StemContentBaseSchema.extend({
    text: TextSchema,
    options: z.array(OptionSchema),
  }),
  config: z
    .object({
      allowMultipleKeys: z.boolean().optional(),
    })
    .optional(),
  scoring: z
    .object({
      options: z.record(
        z.object({
          isKey: z.boolean(),
        }),
      ),
    })
    .optional(),
});

export const StemTextExtendedSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.TextExtended),
  config: z
    .object({
      enableCustomScoring: z.boolean().optional(),
      enableRubric: z.boolean().optional(),
      enableSolution: z.boolean().optional(),
    })
    .optional(),
});

export const StemMatchSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.Match),
  content: StemContentBaseSchema.extend({
    optionsOne: z.array(OptionSchema),
    optionsTwo: z.array(OptionSchema),
  }),
  scoring: z
    .object({
      pairs: z.array(PairSchema),
    })
    .optional(),
});

export const StemMatchTableSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.MatchTable),
  content: StemContentBaseSchema.extend({
    stems: z.array(z.any()),
    options: z.array(OptionSchema),
  }),
  scoring: z
    .object({
      matchOptions: z.any(),
    })
    .optional(),
});

export const StemOrderSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.Order),
  content: StemContentBaseSchema.extend({
    options: z.array(OptionSchema),
  }),
  scoring: z
    .object({
      order: z.array(z.string()),
    })
    .optional(),
});

export const StemGriddedSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.Gridded),
  config: z
    .object({
      layout: z.object({
        columnCount: z.number(),
        rangeMin: z.number(),
        rangeMax: z.number(),
      }),
    })
    .optional(),
});

export const StemFractionModelSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.FractionModel),
  config: z
    .object({
      model: z.object({
        shape: FractionShapeEnum,
      }),
    })
    .optional(),
});

export const StemSliderSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.Slider),
  config: z
    .object({
      slider: z.object({
        min: z.string(),
        max: z.string(),
        step: z.string(),
      }),
    })
    .optional(),
});

export const StemCartesianGraphSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.CartesianGraph),
  config: z
    .object({
      graph: z.object({
        width: z.number(),
        height: z.number(),
        x: z.object({
          min: z.number(),
          max: z.number(),
          step: z.number().optional(),
        }),
        y: z.object({
          min: z.number(),
          max: z.number(),
          step: z.number().optional(),
        }),
      }),
    })
    .optional(),
});

export const StemNumberLinePlotSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.NumberLinePlot),
  config: z
    .object({
      numberLine: z.object({
        min: z.number(),
        max: z.number(),
      }),
    })
    .optional(),
});

export const StemAssociateSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.Associate),
  content: StemContentBaseSchema.extend({
    options: z.array(OptionSchema),
  }),
});

export const StemDrawingSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.Drawing),
  content: StemContentBaseGraphicSchema.extend({
    tokens: z.array(TokenSchema).optional(),
  }),
  config: z
    .object({
      responseArea: z
        .object({
          width: z.number().optional(),
          height: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
});

export const StemFractionModelConstructSchema = StemBaseSchema.extend({
  interactionType: z.literal(InteractionTypeEnum.FractionModelConstruct),
  content: StemContentBaseSchema,
  config: z
    .object({
      model: z
        .object({
          shape: FractionShapeEnum,
          rows: z.string(),
          columns: z.string(),
          groups: z.string().optional(),
          slices: z.string(),
          caption: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
});

export const StemSchema: z.ZodDiscriminatedUnion<
  "interactionType",
  [
    typeof StemChoiceSchema,
    typeof StemBarChartSchema,
    typeof StemLineChartSchema,
    typeof StemGraphicHotspotSchema,
    typeof StemGraphicPositionObjectSchema,
    typeof StemGraphicAssociateSchema,
    typeof StemTextSelectableSchema,
    typeof StemTextExtendedSchema,
    typeof StemAudioSchema,
    typeof StemTextGapMatchSchema,
    typeof StemGraphicGapMatchSchema,
    typeof StemGraphicSelectPointSchema,
    typeof StemTextInlineSchema,
    typeof StemGraphicOrderSchema,
    typeof StemCartesianInequalitySchema,
    typeof StemMatchSchema,
    typeof StemMatchTableSchema,
    typeof StemOrderSchema,
    typeof StemGriddedSchema,
    typeof StemFractionModelSchema,
    typeof StemSliderSchema,
    typeof StemCartesianGraphSchema,
    typeof StemNumberLinePlotSchema,
    typeof StemAssociateSchema,
    typeof StemDrawingSchema,
    typeof StemFractionModelConstructSchema,
  ]
> = z.discriminatedUnion("interactionType", [
  StemChoiceSchema,
  StemBarChartSchema,
  StemLineChartSchema,
  StemGraphicHotspotSchema,
  StemGraphicPositionObjectSchema,
  StemGraphicAssociateSchema,
  StemTextSelectableSchema,
  StemTextExtendedSchema,
  StemAudioSchema,
  StemTextGapMatchSchema,
  StemGraphicGapMatchSchema,
  StemGraphicSelectPointSchema,
  StemTextInlineSchema,
  StemGraphicOrderSchema,
  StemCartesianInequalitySchema,
  StemMatchSchema,
  StemMatchTableSchema,
  StemOrderSchema,
  StemGriddedSchema,
  StemFractionModelSchema,
  StemSliderSchema,
  StemCartesianGraphSchema,
  StemNumberLinePlotSchema,
  StemAssociateSchema,
  StemDrawingSchema,
  StemFractionModelConstructSchema,
]);
